import type { InsightsPeriod } from "@/api/client";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

/** The three period options the Insights drawers expose, defaulting to This
 * Month. "all" is intentionally NOT offered as a tab — the toggle is always one
 * of the three windows; the default re-seeds to "month" each time a drawer
 * opens (the parent owns that). */
const PERIOD_TABS: { value: Exclude<InsightsPeriod, "all">; label: string }[] = [
  { value: "month", label: "This Month" },
  { value: "quarter", label: "This Quarter" },
  { value: "year", label: "This Year" },
];

export interface PeriodToggleProps {
  /** Current period; one of month | quarter | year. */
  value: Exclude<InsightsPeriod, "all">;
  /** Fired when the user picks a different window. */
  onChange: (next: Exclude<InsightsPeriod, "all">) => void;
  /** Stable testid for the toggle group (defaults to "insights-period"). */
  testId?: string;
}

/** Shared This Month / This Quarter / This Year period toggle for the Insights
 * drawers (deals / growth). Uses the same Tabs idiom as the status filters so
 * the interaction is identical across drawers. */
export function PeriodToggle({ value, onChange, testId = "insights-period" }: PeriodToggleProps) {
  return (
    <Tabs
      value={value}
      onValueChange={(next) => {
        if (next === "" || next === value) return;
        onChange(next as Exclude<InsightsPeriod, "all">);
      }}
      activationMode="manual"
      data-testid={testId}
    >
      <TabsList>
        {PERIOD_TABS.map((t) => (
          <TabsTrigger key={t.value} value={t.value} data-testid={`${testId}-${t.value}`}>
            {t.label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}
